import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import Slider from 'react-slick';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import HeroCopyControl from '../../asstes/Hero-Copy-control.png';
import HeroCopyVariant from '../../asstes/Hero-Copy-variant.png';
import CTAControl from '../../asstes/CTA Moved Above the Fold-control.png';
import CTAVariant from '../../asstes/CTA Moved Above the Fold-variant.png';
import TrustBadgesControl from '../../asstes/Trust Badges-control.png';
import TrustBadgesVariant from '../../asstes/Trust Badges-variant.png';
import SecureCheckoutControl from '../../asstes/Secure checkout-control.png';
import SecureCheckoutVariant from '../../asstes/Secure checkout-variant.png';
import ProductPageControl from '../../asstes/Product Page-control.png';
import ProductPageVariant from '../../asstes/Product Page-variant.png';
import popupControl from '../../asstes/popup-control.png';
import popupVariant from '../../asstes/popup-variant.png';

// ── Clock Icon ───────────────────────────────────────────────────────────────
function IconClock() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
        >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
        </svg>
    );
}

const experiments = [
    {
        id: 1,
        title: 'CTA Moved Above the Fold',
        hypothesis: 'Placing the primary CTA above the fold on mobile reduces scroll distance and catches attention immediately.',
        duration: 'Tested over 30 days',
        controlImg: CTAControl,
        variantImg: CTAVariant,
        metricsControl: ['CTR: 1.1%', 'Bounce: 74%'],
        metricsVariant: [{ arrow: '↑', text: 'CTR: 1.8%' }, { arrow: '↓', text: 'Bounce: 65%' }],
    },
    {
        id: 2,
        title: 'Trust Badges Near Price',
        hypothesis: 'Adding security badges and a "30-day money back" guarantee next to the price reduces purchase anxiety.',
        duration: 'Ran for 45 days',
        controlImg: TrustBadgesControl,
        variantImg: TrustBadgesVariant,
        metricsControl: ['ATC: 1.8%', 'Rev/Visit: $1.20'],
        metricsVariant: [{ arrow: '↑', text: 'ATC: 2.3%' }, { arrow: '↑', text: 'Rev/Visit: $1.45' }],
    },
    {
        id: 3,
        title: 'Simplified Checkout Flow',
        hypothesis: 'Reducing checkout from 4 steps to 2 and removing optional fields cuts friction at the most critical conversion point.',
        duration: 'Tested over 2 months',
        controlImg: SecureCheckoutControl,
        variantImg: SecureCheckoutVariant,
        metricsControl: ['Completion: 12%', 'Abandon: 88%'],
        metricsVariant: [{ arrow: '↑', text: 'Completion: 19%' }, { arrow: '↓', text: 'Abandon: 81%' }],
    },
    {
        id: 4,
        title: 'Hero Copy Personalization',
        hypothesis: 'Using AI-generated audience-specific headlines based on traffic source (Facebook vs Google) creates immediate relevance.',
        duration: 'Ran for 60 days',
        controlImg: HeroCopyControl,
        variantImg: HeroCopyVariant,
        metricsControl: ['Scroll: 34%', 'Engage: 1.2s'],
        metricsVariant: [{ arrow: '↑', text: 'Scroll: 45%' }, { arrow: '↑', text: 'Engage: 2.1s' }],
    },
    {
        id: 5,
        title: 'Product Page Image Gallery',
        hypothesis: 'Enlarging the primary product image and adding price directly on the CTA button reduces decision friction.',
        duration: 'Tested over 40 days',
        controlImg: ProductPageControl,
        variantImg: ProductPageVariant,
        metricsControl: ['ATC: 1.2%', 'Time: 45s'],
        metricsVariant: [{ arrow: '↑', text: 'ATC: 1.9%' }, { arrow: '↓', text: 'Time: 32s' }],
    },
    {
        id: 6,
        title: 'Exit-Intent Popup Redesign',
        hypothesis: 'Replacing generic "Subscribe" popup with a discount-focused exit-intent offer with urgency timer captures leaving visitors.',
        duration: 'Ran for 3 months',
        controlImg: popupControl,
        variantImg: popupVariant,
        metricsControl: ['Capture: 0.8%', 'Close: 99%'],
        metricsVariant: [{ arrow: '↑', text: 'Capture: 1.4%' }, { arrow: '↓', text: 'Close: 88%' }],
    },
];

// ── Before/After Comparison Slider Component ─────────────────────────────────
function BeforeAfterSlider({ beforeImg, afterImg }) {
    const [sliderPos, setSliderPos] = useState(50);
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef(null);

    const getPos = useCallback((clientX) => {
        const rect = containerRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
        return (x / rect.width) * 100;
    }, []);

    const onMouseDown = (e) => { e.preventDefault(); setIsDragging(true); };
    const onMouseMove = useCallback((e) => { if (isDragging) setSliderPos(getPos(e.clientX)); }, [isDragging, getPos]);
    const onMouseUp = useCallback(() => setIsDragging(false), []);

    const onTouchStart = () => setIsDragging(true);
    const onTouchMove = useCallback((e) => {
        if (isDragging && e.touches[0]) setSliderPos(getPos(e.touches[0].clientX));
    }, [isDragging, getPos]);
    const onTouchEnd = useCallback(() => setIsDragging(false), []);

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
            window.addEventListener('touchmove', onTouchMove);
            window.addEventListener('touchend', onTouchEnd);
        }
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('touchend', onTouchEnd);
        };
    }, [isDragging, onMouseMove, onMouseUp, onTouchMove, onTouchEnd]);

    return (
        <div
            ref={containerRef}
            className="ba-container"
            style={{ userSelect: 'none', cursor: isDragging ? 'ew-resize' : 'default' }}
        >
            {/* After/Background — full width, natural height, drives container size */}
            <img src={afterImg} alt="After" className="ba-img ba-img-before" draggable={false} />

            {/* Before/Clipped — absolutely overlaid, clipped from the right */}
            <div className="ba-after-clip" style={{ width: `${sliderPos}%` }}>
                <img src={beforeImg} alt="Before" className="ba-img ba-img-after" draggable={false} />
            </div>

            {/* Divider Line */}
            <div className="ba-divider" style={{ left: `${sliderPos}%` }}>
                <div
                    className="ba-handle"
                    onMouseDown={onMouseDown}
                    onTouchStart={onTouchStart}
                >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <path d="M9 18L3 12L9 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M15 6L21 12L15 18" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
            </div>

            {/* Labels */}
            <div className={`ba-label ba-label-before ${sliderPos < 15 ? 'hide-label' : ''}`}>Before</div>
            <div className={`ba-label ba-label-after ${sliderPos > 85 ? 'hide-label' : ''}`}>After ✦</div>
        </div>
    );
}

// ── Popup Modal ───────────────────────────────────────────────────────────────
function ExperimentModal({ experiment, onClose }) {
    useBodyScrollLock(); // lock body scroll while modal is open

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('keydown', onKey);
        };
    }, [onClose]);

    return (
        <div className="exp-modal-overlay" onClick={onClose}>
            <div className="exp-modal-box" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="exp-modal-header">
                    <div>
                        <span className="exp-modal-label">Real Experiment</span>
                        <h3 className="exp-modal-title">{experiment.title}</h3>
                    </div>
                    <button className="exp-modal-close" onClick={onClose} aria-label="Close">✕</button>
                </div>

                {/* Before / After Slider */}
                <BeforeAfterSlider beforeImg={experiment.controlImg} afterImg={experiment.variantImg} />

                {/* Footer info */}
                <div className="exp-modal-footer">
                    <p className="exp-modal-hypothesis">{experiment.hypothesis}</p>
                    {experiment.duration && (
                        <p className="exp-modal-duration" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', color: '#a78bfa', marginTop: '-8px', marginBottom: '12px', fontWeight: '600' }}>
                            <IconClock /> {experiment.duration}
                        </p>
                    )}
                    <div className="exp-modal-metrics">
                        <div className="exp-modal-metrics-group">
                            <span className="exp-metrics-label">Control</span>
                            {experiment.metricsControl.map((m, i) => (
                                <span key={i} className="ab-metric-pill">{m}</span>
                            ))}
                        </div>
                        <div className="exp-modal-metrics-divider">→</div>
                        <div className="exp-modal-metrics-group">
                            <span className="exp-metrics-label winner">Winner</span>
                            {experiment.metricsVariant.map((m, i) => (
                                <span key={i} className="ab-metric-pill">
                                    <span className="pill-arrow">{m.arrow}</span> {m.text}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Main Showcase Component ───────────────────────────────────────────────────
export default function CroShowcase() {
    const [activeExp, setActiveExp] = useState(null);

    return (
        <section className="cro-ab-showcase">
            <Container>
                <div className="section-header cro-reveal">
                    <span className="cro-section-label">Real Experiments</span>
                    <h2 className="cro-section-title">
                        A/B test examples that <span className="highlight">prove results</span>
                    </h2>
                </div>
                <p className="section-subtitle-center cro-reveal">
                    Hover over each card to see how the winning variant outperformed the control. Every test is backed by real data.
                </p>

                <div className="ab-slider-wrapper cro-reveal">
                    <Slider
                        dots={true}
                        infinite={true}
                        speed={500}
                        slidesToShow={3}
                        slidesToScroll={1}
                        autoplay={true}
                        autoplaySpeed={4000}
                        pauseOnHover={true}
                        responsive={[
                            { breakpoint: 992, settings: { slidesToShow: 2 } },
                            { breakpoint: 576, settings: { slidesToShow: 1 } },
                        ]}
                    >
                        {experiments.map((exp) => (
                            <div key={exp.id}>
                                <div className="ab-experiment-card">
                                    <div className="ab-preview-area">
                                        <span className="ab-state-tag tag-control">Control</span>
                                        <span className="ab-state-tag tag-variant">Winner</span>
                                        <div className="ab-mockup-control">
                                            <img src={exp.controlImg} alt="Control" className="ab-placeholder-img" />
                                        </div>
                                        <div className="ab-mockup-variant">
                                            <img src={exp.variantImg} alt="Variant" className="ab-placeholder-img" />
                                        </div>
                                    </div>
                                    <div className="ab-card-info">
                                        <h4>{exp.title}</h4>
                                        <p className="ab-hypothesis">{exp.hypothesis}</p>
                                        {exp.duration && (
                                            <p className="ab-duration" style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.8rem', color: '#a78bfa', marginTop: '-5px', marginBottom: '10px', fontWeight: '600' }}>
                                                <IconClock /> {exp.duration}
                                            </p>
                                        )}
                                        <div className="ab-metrics-row">
                                            <div className="ab-metrics-control">
                                                {exp.metricsControl.map((m, i) => (
                                                    <span key={i} className="ab-metric-pill">{m}</span>
                                                ))}
                                            </div>
                                            <div className="ab-metrics-variant">
                                                {exp.metricsVariant.map((m, i) => (
                                                    <span key={i} className="ab-metric-pill">
                                                        <span className="pill-arrow">{m.arrow}</span> {m.text}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        {/* ── More Info Button ── */}
                                        <button
                                            className="ab-more-info-btn"
                                            onClick={() => setActiveExp(exp)}
                                        >
                                            <span>View Before &amp; After</span>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </Slider>
                </div>
            </Container>

            {/* ── Popup Modal ── */}
            {activeExp && (
                <ExperimentModal experiment={activeExp} onClose={() => setActiveExp(null)} />
            )}
        </section>
    );
}
